import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";

import { contrastInk } from "../../theme/color";

/**
 * Grade de horários (visões Dia e Semana).
 *
 * Geometria: a coluna é um dia, o eixo vertical é o tempo. O card é
 * posicionado por `top` a partir do horário de início e dimensionado por
 * `height` a partir da duração — a altura do card *é* a duração.
 *
 * Nota de layout: tudo aqui usa **altura fixa em pixels**, nunca `flex: 1` com
 * `minHeight: 0`. Uma primeira versão encadeava flex + `overflow` nesta árvore
 * e travava a thread de layout do navegador; medir em pixels remove a
 * realimentação entre altura do conteúdo e barra de rolagem.
 */

const SNAP_MINUTES = 15;
const MIN_DURATION_MINUTES = 15;
const DEFAULT_DURATION_MINUTES = 60;
const HOUR_HEIGHT = 48;
const GUTTER_WIDTH = 56;
const HEADER_HEIGHT = 48;
const DRAG_THRESHOLD_PX = 4;
const START_HOUR = 6;
const END_HOUR = 23;

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },

  header: {
    display: "flex",
    height: HEADER_HEIGHT,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.06)"
        : theme.palette.grey[50],
  },
  headerGutter: { width: GUTTER_WIDTH, flexShrink: 0 },
  headerDay: {
    width: 0,
    flexGrow: 1,
    textAlign: "center",
    padding: "4px 2px",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  headerWeekday: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  headerNumber: { fontSize: 16, fontWeight: 600, lineHeight: 1.3 },
  headerToday: {
    display: "inline-block",
    minWidth: 24,
    height: 24,
    lineHeight: "24px",
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  scroller: { overflowY: "auto", overflowX: "hidden" },
  board: { display: "flex", position: "relative" },

  gutter: { width: GUTTER_WIDTH, flexShrink: 0, position: "relative" },
  hourLabel: {
    position: "absolute",
    right: 8,
    marginTop: -7,
    fontSize: 11,
    color: theme.palette.text.hint,
    fontVariantNumeric: "tabular-nums",
  },

  column: {
    width: 0,
    flexGrow: 1,
    position: "relative",
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  columnWeekend: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.04)"
        : theme.palette.grey[50],
  },
  hourLine: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  nowLine: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 0,
    borderTop: `2px solid ${theme.palette.error.main}`,
    zIndex: 3,
  },

  event: {
    position: "absolute",
    borderRadius: 5,
    padding: "1px 5px",
    overflow: "hidden",
    fontSize: 11,
    lineHeight: 1.25,
    zIndex: 2,
    userSelect: "none",
    touchAction: "none",
    boxShadow: "0 1px 2px rgba(16,24,40,0.2)",
  },
  eventDraggable: { cursor: "grab" },
  eventActive: { zIndex: 5, boxShadow: "0 6px 16px rgba(16,24,40,0.35)" },
  eventTitle: {
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  eventTime: { fontVariantNumeric: "tabular-nums", opacity: 0.9 },

  handle: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 6,
    cursor: "ns-resize",
  },
  handleBar: {
    position: "absolute",
    left: "50%",
    bottom: 2,
    marginLeft: -12,
    width: 24,
    height: 3,
    borderRadius: 2,
    backgroundColor: "currentColor",
    opacity: 0.5,
  },

  hint: {
    padding: theme.spacing(1, 2),
    fontSize: 12,
    color: theme.palette.text.secondary,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const durationOf = (ev) =>
  ev.endDate && ev.endDate > ev.date
    ? Math.round((ev.endDate - ev.date) / 60000)
    : DEFAULT_DURATION_MINUTES;

/** Distribui eventos sobrepostos em colunas lado a lado. */
const layoutOverlaps = (list) => {
  const sorted = [...list].sort((a, b) => a.top - b.top || b.bottom - a.bottom);
  const out = [];
  let group = [];
  let groupEnd = -1;

  const flush = () => {
    if (!group.length) return;
    const lanes = [];
    group.forEach((ev) => {
      let lane = lanes.findIndex((endAt) => endAt <= ev.top);
      if (lane === -1) {
        lane = lanes.length;
        lanes.push(ev.bottom);
      } else {
        lanes[lane] = ev.bottom;
      }
      ev.lane = lane;
    });
    group.forEach((ev) => {
      ev.lanes = lanes.length;
      out.push(ev);
    });
    group = [];
    groupEnd = -1;
  };

  sorted.forEach((ev) => {
    if (groupEnd !== -1 && ev.top >= groupEnd) flush();
    group.push(ev);
    groupEnd = groupEnd === -1 ? ev.bottom : Math.max(groupEnd, ev.bottom);
  });
  flush();
  return out;
};

const AgendaTimeGrid = ({
  days,
  events,
  height,
  onEventClick,
  onEventChange,
  canEditEvent,
}) => {
  const classes = useStyles();
  const boardRef = useRef(null);
  const movedRef = useRef(false);
  // O arraste vive numa ref e é só espelhado no estado para render. Ler do
  // estado dentro do pointermove perde eventos que chegam antes do React
  // reconciliar o pointerdown — o movimento era descartado e o pointerup
  // interpretava como clique.
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const applyDrag = useCallback((next) => {
    dragRef.current = next;
    setDrag(next);
  }, []);

  const boardHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;
  const pxPerMinute = HOUR_HEIGHT / 60;
  const scrollerHeight = Math.max(200, height - HEADER_HEIGHT);

  const hours = useMemo(() => {
    const out = [];
    for (let h = START_HOUR; h <= END_HOUR; h += 1) out.push(h);
    return out;
  }, []);

  const editable = useCallback(
    (ev) => (canEditEvent ? !!canEditEvent(ev) : false),
    [canEditEvent]
  );

  const snap = useCallback(
    (px) => Math.round(px / pxPerMinute / SNAP_MINUTES) * SNAP_MINUTES,
    [pxPerMinute]
  );

  const columnFromX = useCallback(
    (clientX) => {
      const node = boardRef.current;
      if (!node) return null;
      const rect = node.getBoundingClientRect();
      const usable = rect.width - GUTTER_WIDTH;
      if (usable <= 0) return null;
      const idx = Math.floor(
        ((clientX - rect.left - GUTTER_WIDTH) / usable) * days.length
      );
      return Math.min(days.length - 1, Math.max(0, idx));
    },
    [days.length]
  );

  const onPointerDown = (e, ev, mode) => {
    if (!editable(ev)) return;
    e.stopPropagation();
    // setPointerCapture lança se o ponteiro não estiver ativo. Fica em
    // try/catch e depois do resto: sem isso, uma exceção aqui abortava o
    // início do arraste inteiro.
    movedRef.current = false;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (err) {
      /* captura é otimização, não requisito */
    }
    applyDrag({
      id: ev.id,
      mode,
      pointerId: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      startAt: new Date(ev.date),
      duration: durationOf(ev),
      dayIndex: days.findIndex((d) => sameDay(d, ev.date)),
      previewStart: new Date(ev.date),
      previewDuration: durationOf(ev),
      previewDay: days.findIndex((d) => sameDay(d, ev.date)),
    });
  };

  const onPointerMove = (e) => {
    const d0 = dragRef.current;
    if (!d0 || e.pointerId !== d0.pointerId) return;
    const dy = e.clientY - d0.y0;
    const dx = e.clientX - d0.x0;
    if (Math.abs(dy) > DRAG_THRESHOLD_PX || Math.abs(dx) > DRAG_THRESHOLD_PX) {
      movedRef.current = true;
    }

    if (d0.mode === "move") {
      const base = minutesOf(d0.startAt) + snap(dy);
      const clamped = Math.min(
        Math.max(base, START_HOUR * 60),
        END_HOUR * 60 - d0.duration
      );
      const next = new Date(d0.startAt);
      next.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0);
      const col = columnFromX(e.clientX);
      applyDrag({
        ...d0,
        previewStart: next,
        previewDay: col == null ? d0.previewDay : col,
      });
    } else {
      const raw = d0.duration + snap(dy);
      const max = END_HOUR * 60 - minutesOf(d0.startAt);
      applyDrag({
        ...d0,
        previewDuration: Math.min(Math.max(raw, MIN_DURATION_MINUTES), max),
      });
    }
  };

  const onPointerUp = (e, ev) => {
    const finished = dragRef.current;
    if (!finished || e.pointerId !== finished.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (err) {
      /* idem */
    }
    const moved = movedRef.current;
    applyDrag(null);

    if (!moved) {
      if (onEventClick) onEventClick(ev);
      return;
    }

    const targetDay = days[finished.previewDay] || days[0];
    const start = new Date(targetDay);
    start.setHours(
      finished.previewStart.getHours(),
      finished.previewStart.getMinutes(),
      0,
      0
    );
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + finished.previewDuration);

    if (
      start.getTime() === finished.startAt.getTime() &&
      finished.previewDuration === finished.duration
    ) {
      return;
    }
    if (onEventChange) onEventChange(ev, start, end);
  };

  const now = new Date();

  const placedByDay = useMemo(() => {
    const map = days.map(() => []);
    events.forEach((ev) => {
      const dragging = drag && drag.id === ev.id;
      const start = dragging ? drag.previewStart : ev.date;
      const duration = dragging ? drag.previewDuration : durationOf(ev);
      let dayIndex = dragging && drag.mode === "move" ? drag.previewDay : -1;
      if (dayIndex === -1) {
        dayIndex = days.findIndex((d) => sameDay(d, ev.date));
      }
      if (dayIndex < 0 || dayIndex >= days.length) return;
      map[dayIndex].push({
        ...ev,
        previewStart: start,
        previewDuration: duration,
        top: minutesOf(start),
        bottom: minutesOf(start) + duration,
      });
    });
    return map.map((list) => layoutOverlaps(list));
  }, [days, events, drag]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headerGutter} />
        {days.map((d) => (
          <div key={d.toISOString()} className={classes.headerDay}>
            <div className={classes.headerWeekday}>
              {d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}
            </div>
            <div className={classes.headerNumber}>
              <span className={sameDay(d, now) ? classes.headerToday : undefined}>
                {d.getDate()}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={classes.scroller} style={{ height: scrollerHeight }}>
        <div
          className={classes.board}
          ref={boardRef}
          style={{ height: boardHeight }}
        >
          <div className={classes.gutter}>
            {hours.map((h) => (
              <span
                key={h}
                className={classes.hourLabel}
                style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
              >
                {pad(h)}:00
              </span>
            ))}
          </div>

          {days.map((day, dayIndex) => {
            const weekend = day.getDay() === 0 || day.getDay() === 6;
            const showNow =
              sameDay(day, now) &&
              minutesOf(now) >= START_HOUR * 60 &&
              minutesOf(now) <= END_HOUR * 60;

            return (
              <div
                key={day.toISOString()}
                className={
                  weekend
                    ? `${classes.column} ${classes.columnWeekend}`
                    : classes.column
                }
              >
                {hours.map((h) => (
                  <div
                    key={h}
                    className={classes.hourLine}
                    style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}
                  />
                ))}

                {showNow && (
                  <div
                    className={classes.nowLine}
                    style={{
                      top: (minutesOf(now) - START_HOUR * 60) * pxPerMinute,
                    }}
                  />
                )}

                {placedByDay[dayIndex].map((ev) => {
                  const top = (ev.top - START_HOUR * 60) * pxPerMinute;
                  const boxHeight = Math.max(
                    14,
                    ev.previewDuration * pxPerMinute - 2
                  );
                  const lanes = ev.lanes || 1;
                  const w = 100 / lanes;
                  const canEdit = editable(ev);
                  const active = drag && drag.id === ev.id;
                  const end = new Date(ev.previewStart);
                  end.setMinutes(end.getMinutes() + ev.previewDuration);

                  return (
                    <div
                      key={ev.id}
                      className={[
                        classes.event,
                        canEdit ? classes.eventDraggable : "",
                        active ? classes.eventActive : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        top,
                        height: boxHeight,
                        left: `${(ev.lane || 0) * w}%`,
                        width: `${w}%`,
                        backgroundColor: ev.color,
                        color: contrastInk(ev.color),
                      }}
                      title={
                        canEdit
                          ? `${fmtTime(ev.previewStart)} – ${fmtTime(end)} · ${ev.title}`
                          : `${ev.title} — origem externa, edite na tarefa/sprint`
                      }
                      onPointerDown={(e) => onPointerDown(e, ev, "move")}
                      onPointerMove={onPointerMove}
                      onPointerUp={(e) => onPointerUp(e, ev)}
                      onClick={() => {
                        if (!canEdit && onEventClick) onEventClick(ev);
                      }}
                    >
                      <div className={classes.eventTitle}>{ev.title}</div>
                      {boxHeight > 28 && (
                        <div className={classes.eventTime}>
                          {fmtTime(ev.previewStart)} – {fmtTime(end)}
                        </div>
                      )}
                      {canEdit && boxHeight > 20 && (
                        <div
                          className={classes.handle}
                          onPointerDown={(e) => onPointerDown(e, ev, "resize")}
                          onPointerMove={onPointerMove}
                          onPointerUp={(e) => onPointerUp(e, ev)}
                        >
                          <div className={classes.handleBar} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className={classes.hint}>
        Arraste um compromisso para mudar horário ou dia. Arraste a borda
        inferior para alterar a duração. Encaixe de {SNAP_MINUTES} minutos.
      </div>
    </div>
  );
};

AgendaTimeGrid.propTypes = {
  days: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  events: PropTypes.array,
  /** Altura total disponível, em pixels. Medida pela página, não por flex. */
  height: PropTypes.number,
  onEventClick: PropTypes.func,
  /** `(evento, novoInicio, novoFim)` — chamado uma vez, ao soltar. */
  onEventChange: PropTypes.func,
  canEditEvent: PropTypes.func,
};

AgendaTimeGrid.defaultProps = {
  events: [],
  height: 560,
  onEventClick: undefined,
  onEventChange: undefined,
  canEditEvent: undefined,
};

export default AgendaTimeGrid;
